import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { ConvexQueryClient } from "@convex-dev/react-query";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HotkeysProvider } from "react-hotkeys-hook";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Convex client
import { convex } from "./lib/convex";
import { ConvexProviderWithAuthKit } from "./components/auth/convex-provider-with-authkit";

// Theme provider
import { ThemeProvider } from "./lib/theme";
import { ConfirmProvider } from "./components/confirm-provider";

import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
import { Sentry, initSentry } from "./lib/sentry";
import { resolveReturnTo } from "./lib/auth-return-to";

// Setup TanStack Query with Convex
const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
      staleTime: Infinity,
    },
  },
});
convexQueryClient.connect(queryClient);

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

initSentry(router);

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("app");
const workosApiHostname = import.meta.env.VITE_WORKOS_API_HOSTNAME?.trim() || undefined;
const workosDevMode = import.meta.env.VITE_WORKOS_DEV_MODE === "true";

function handleAuthRedirect({ state }: { state: unknown }) {
  const returnTo = resolveReturnTo(state);
  if (!returnTo) return;
  // AuthKit rewrites the URL to strip the auth code right after this callback; navigate after it.
  window.setTimeout(() => {
    void router.navigate({ href: returnTo, replace: true });
  }, 0);
}
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ThemeProvider defaultTheme="field">
        <AuthKitProvider
          clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
          redirectUri={import.meta.env.VITE_WORKOS_REDIRECT_URI}
          apiHostname={workosApiHostname}
          devMode={workosDevMode}
          onRedirectCallback={handleAuthRedirect}
        >
          <HotkeysProvider>
            <ConvexProviderWithAuthKit client={convex} useAuth={useAuth}>
              <QueryClientProvider client={queryClient}>
                <Sentry.ErrorBoundary fallback={<div className="p-6 text-sm text-destructive">Something went wrong.</div>}>
                  <ConfirmProvider>
                    <RouterProvider router={router} />
                  </ConfirmProvider>
                </Sentry.ErrorBoundary>
              </QueryClientProvider>
            </ConvexProviderWithAuthKit>
          </HotkeysProvider>
        </AuthKitProvider>
      </ThemeProvider>
    </StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
